<template>
  <div class="space-y-4">
    <div class="mb-8 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <h2>{{ invoiceHeadingLabel }} {{ invoice.completeInvoiceNumber }}</h2>
        <InvoiceStatusBadge :status="invoice.status" size="md" />
      </div>
      <Spinner v-if="isFetching" class="h-6 w-6" />
    </div>

    <div class="flex items-start justify-between">
      <div>
        <LabeledText :label="t('receiver')">
          <span v-if="invoice.receiverData.company">
            {{ invoice.receiverData.company }}
            <br />
          </span>
          {{ invoice.receiverData.firstName }}
          {{ invoice.receiverData.lastName }}
          <br />
          {{ invoice.receiverData.street }}
          <br />
          {{ invoice.receiverData.zip }}
          {{ invoice.receiverData.city }}
          <template v-if="invoice.receiverData.vatId">
            <br />
            {{ t('vatId') }}: {{ invoice.receiverData.vatId }}
          </template>
        </LabeledText>

        <LabeledText :label="`${t('bookingNumber')}`" class="mt-4">
          <router-link
            v-if="invoice.booking"
            :to="{ name: 'bookingEdit', params: { id: invoice.booking.id } }"
            class="text-link"
          >
            {{ invoice.booking.bookingNumber }}
          </router-link>
          <span v-else>n/a</span>
        </LabeledText>

        <LabeledText
          v-if="displayCounterpartyType && !canEditCounterpartyType"
          :label="t('counterpartyType.label')"
          class="mt-4"
        >
          {{ t(`counterpartyTypes.${displayCounterpartyType}`) }}
        </LabeledText>
      </div>

      <div class="grid grid-cols-1 gap-2">
        <div>
          <CVButton outline class="w-full" @click="downloadInvoice(invoice)">{{
            t('downloadInvoice')
          }}</CVButton>
        </div>
        <div>
          <CVButton outline class="w-full" @click="downloadInvoiceDirect(invoice)">{{
            t('downloadInvoiceDirect')
          }}</CVButton>
        </div>
        <div v-if="invoice.status !== InvoiceStatus.CANCELED">
          <CVButton
            outline
            class="w-full"
            :is-loading="isSending"
            @click="sendInvoiceToCustomerWithAlert(invoice.id)"
            >{{ t('sendToCustomer') }}</CVButton
          >
        </div>
        <div v-if="canRegenerateInvoicePdf(invoice)">
          <CVButton
            outline
            class="w-full"
            :is-loading="isRegeneratingPdf"
            @click="regenerateInvoicePdf(invoice.id)"
            >{{ regeneratePdfLabel }}</CVButton
          >
        </div>
      </div>
    </div>

    <div class="mt-4 flex justify-between">
      <LabeledText :label="`${t('description')}`">{{ invoice.title ?? '-' }}</LabeledText>

      <LabeledText :label="`${t('invoiceDate')}`">{{ formattedInvoiceDate }}</LabeledText>
    </div>

    <InvoicePositions :invoice="invoice" />

    <div class="flex justify-end gap-4">
      <LabeledText v-if="$can('list', 'InvoiceAllocation')" :label="`${t('openBalance')}`">
        <div class="flex justify-end">
          <span
            v-currency="invoice.currentBalance"
            class="font-bold tabular-nums"
            :class="{
              'text-red-500': invoice.currentBalance > 0,
              'text-success': invoice.currentBalance === 0,
            }"
          />
        </div>
      </LabeledText>
      <LabeledText :label="`${t('allocated')}`">
        <div class="flex justify-end">
          <span
            v-currency="allocatedAmount"
            class="font-bold tabular-nums"
            :class="{
              'text-success': allocatedAmount === invoice.totalPositionSum,
              'text-red-500': allocatedAmount !== invoice.totalPositionSum,
            }"
          />
        </div>
      </LabeledText>
    </div>

    <div v-if="$can('list', 'LedgerEntry')" class="flex justify-end">
      <CVButton variant="secondary" @click="showLedgerEntriesModal = true">
        {{ t('showLedgerEntries') }}
      </CVButton>
    </div>

    <Divider />

    <Payments :booking="invoice.booking" :invoice="invoice" @on-changed="$emit('refetch')" />

    <Divider />

    <div v-if="canEditCounterpartyType" class="grid max-w-md grid-cols-1 gap-4">
      <SelectField
        name="counterpartyType"
        :label="t('counterpartyType.label')"
        :options="counterpartyTypeOptions"
      />
    </div>

    <Divider v-if="canEditCounterpartyType" />

    <h2>{{ t('comments') }}</h2>

    <CommentsPanel hide-title entity-type="Invoice" :entity-id="invoice.id" :entity="invoice" />

    <InputLabel :label="t('legacyComment')">
      <TextareaField name="comment" rows="4" />
    </InputLabel>

    <div class="mt-4 flex justify-end">
      <CVButton variant="default" :is-loading="isSaving" @click="onSubmit">{{
        t('save')
      }}</CVButton>
    </div>

    <div v-if="invoice.status !== InvoiceStatus.CANCELED">
      <Divider />

      <div class="mt-3">
        <CVButton variant="danger" :disabled="isSaving" @click.prevent="cancelInvoice">
          {{ t('cancelInvoice') }}
        </CVButton>
      </div>
    </div>

    <Modal v-model="showLedgerEntriesModal" :content-class="'max-w-6xl'" show-close>
      <template #title>{{ t('ledgerEntries') }}</template>
      <LedgerEntries :invoice-id="invoice.id" />
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import InvoiceStatusBadge from '@/components/badges/InvoiceStatusBadge.vue';
import Payments from '@/components/booking-forms/Payments.vue';
import Divider from '@/components/Divider.vue';
import LabeledText from '@/components/LabeledText.vue';
import TextareaField from '@/components/TextareaField.vue';
import InputLabel from '@/components/InputLabel.vue';
import CommentsPanel from '@/components/comments/CommentsPanel.vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import type { Invoice, UpdateInvoiceDto } from '@/entities/invoices/invoice.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { useForm } from 'vee-validate';
import InvoicePositions from './InvoicePositions.vue';
import { invoiceUpdateSchema } from '@/validation/invoice.schema';
import { Alert } from '@/services/alert.service';
import { useInvoicePdfs } from '@/hooks/use-invoice-pdf';
import { computed, ref } from 'vue';
import { cloneDeep, sumBy } from 'lodash';
import { usePreventLeavingUnsavedForm } from '@/hooks/use-prevent-leaving-unsaved-form';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import SelectField from '@/components/SelectField.vue';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';
import { formHasChanged } from '@/hooks/use-form-has-changed';
import { useInvoiceAllocations } from '@/queries/use-ledger';
import CVButton from '@/components/buttons/CVButton.vue';
import Modal from '@/components/Modal.vue';
import LedgerEntries from '@/components/ledger-entries/LedgerEntries.vue';
import { useAbility } from '@casl/vue';

const props = defineProps<{
  invoice: Invoice;
  isSaving: boolean;
  isFetching: boolean;
}>();

const emit = defineEmits<{
  (e: 'onSubmit', value: UpdateInvoiceDto): void;
  (e: 'onCancel'): void;
  (e: 'refetch'): void;
}>();

const { t } = useI18n();
const ability = useAbility();

const showLedgerEntriesModal = ref(false);
const formattedInvoiceDate = useFormattedDate(props.invoice.invoiceDate, DateTime.DATE_MED);

const initialValues = {
  comment: props.invoice.comment ?? '',
  counterpartyType: props.invoice.counterpartyType ?? undefined,
};

const canEditCounterpartyType = computed(
  () =>
    Boolean(props.invoice.booking) &&
    (props.invoice.status === InvoiceStatus.DRAFT ||
      props.invoice.status === InvoiceStatus.PRELIMINARY),
);

const displayCounterpartyType = computed(() => props.invoice.counterpartyType ?? null);

const { handleSubmit, values: formValues } = useForm({
  initialValues,
  validationSchema: invoiceUpdateSchema,
});

const counterpartyTypeOptions = useCounterpartyTypeOptions(
  computed(() => {
    const types = [CounterpartyType.CUSTOMER];
    if (props.invoice.booking?.agency) {
      types.push(CounterpartyType.AGENCY);
    }
    // OTHER is system-set (e.g. auto insurance invoices); not manually selectable.
    if (formValues.counterpartyType === CounterpartyType.OTHER) {
      types.push(CounterpartyType.OTHER);
    }
    return types;
  }),
);

const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit((values) => {
  unchangedValues.value = cloneDeep(formValues);
  const { counterpartyType, ...rest } = values;
  emit('onSubmit', canEditCounterpartyType.value ? values : rest);
});

usePreventLeavingUnsavedForm(() => formHasChanged(unchangedValues.value, formValues));

const {
  downloadInvoice,
  downloadInvoiceDirect,
  sendInvoiceToCustomerWithAlert,
  isSending,
  canRegenerateInvoicePdf,
  regenerateInvoicePdf,
  isRegeneratingPdf,
  regeneratePdfLabel,
} = useInvoicePdfs();

const { data: invoiceAllocations } = useInvoiceAllocations(
  computed(() => props.invoice.id),
  ability.can('list', 'InvoiceAllocation'),
);
const allocatedAmount = computed(() => {
  return sumBy(invoiceAllocations.value ?? [], 'amount') / 100;
});

const invoiceHeadingLabel = computed(() =>
  props.invoice.type === InvoiceType.SELF_BILLING ? t('invoiceTypes.SELF_BILLING') : t('invoice'),
);

const cancelInvoice = async () => {
  const alertResult = await Alert.fire({
    titleText: t('reallyCancelInvoiceTitle'),
    text: t('reallyCancelInvoiceText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('cancelInvoice'),
    denyButtonText: t('dontCancel'),
  });
  if (alertResult.isConfirmed) {
    emit('onCancel');
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "invoice": "Invoice",
    "receiver": "Receiver",
    "vatId": "VAT ID",
    "bookingNumber": "Booking Number",
    "description": "Description",
    "invoiceDate": "Invoice Date",
    "downloadInvoice": "Show Invoice PDF",
    "downloadInvoiceDirect": "Download Invoice PDF",
    "sendToCustomer": "Send to Customer",
    "comments": "Comments",
    "legacyComment": "Legacy Comment",
    "cancelInvoice": "Cancel Invoice",
    "reallyCancelInvoiceTitle": "Really cancel Invoice?",
    "reallyCancelInvoiceText": "The Invoice will be permanently canceled.",
    "dontCancel": "Don't cancel",
    "payments": "Payments",
    "allocated": "Allocated",
    "ledgerEntries": "Ledger Entries",
    "showLedgerEntries": "Show Ledger Entries",
    "openBalance": "Open Balance"
  },
  "de": {
    "invoice": "Rechnung",
    "receiver": "Empfänger",
    "vatId": "USt-IdNr.",
    "bookingNumber": "Buchungsnummer",
    "description": "Beschreibung",
    "invoiceDate": "Rechnungsdatum",
    "downloadInvoice": "Rechnung anzeigen (PDF)",
    "downloadInvoiceDirect": "Rechnung herunterladen (PDF)",
    "sendToCustomer": "An Kunde senden",
    "comments": "Kommentare",
    "legacyComment": "Alter Kommentar",
    "cancelInvoice": "Rechnung stornieren",
    "reallyCancelInvoiceTitle": "Rechnung wirklich stornieren?",
    "reallyCancelInvoiceText": "Die Rechnung wird unwiderruflich storniert.",
    "dontCancel": "Nicht stornieren",
    "payments": "Zahlungen",
    "allocated": "Zugewiesen",
    "ledgerEntries": "Forderungseinträge",
    "showLedgerEntries": "Forderungseinträge anzeigen",
    "openBalance": "Offener Betrag"
  },
  "es": {
    "invoice": "Factura",
    "receiver": "Destinatario",
    "bookingNumber": "N.º de reserva",
    "description": "Descripción",
    "invoiceDate": "Fecha de factura",
    "downloadInvoice": "Ver factura (PDF)",
    "downloadInvoiceDirect": "Descargar factura (PDF)",
    "sendToCustomer": "Enviar al cliente",
    "comments": "Comentarios",
    "legacyComment": "Comentario anterior",
    "cancelInvoice": "Anular factura",
    "reallyCancelInvoiceTitle": "¿Seguro que desea anular la factura?",
    "reallyCancelInvoiceText": "La factura se anulará de forma definitiva.",
    "dontCancel": "No anular",
    "payments": "Pagos",
    "allocated": "Asignado",
    "ledgerEntries": "Asientos contables",
    "showLedgerEntries": "Ver asientos contables",
    "openBalance": "Saldo pendiente"
  }
}
</i18n>
